// components/ReferalAllTransactions.jsx

import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Define your API base URL
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
// [CASH-MODE] UPI / Cash filter + indication
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeLabel, paymentModeParam, totalsByModeFromRows } from '../../utils/paymentMode';
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';

const ReferalAllTransactions = () => {
  // State variables
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1); // Current page number (1-based)
  const [pageSize, setPageSize] = useState(20); // Items per page
  const [total, setTotal] = useState(0); // Total number of records
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [totalsByMode, setTotalsByMode] = useState(null); // [CASH-MODE] API totalsByMode when provided
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Function to fetch all referral transactions
  const fetchAllReferralTransactions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/referralActivities/transactions`, {
        params: {
          page,
          limit: pageSize,
          paymentMode: paymentModeParam(paymentMode), // [CASH-MODE] undefined for ALL
        },
      });

      if (response.status === 200) {
        const data = response.data;
        // Map the transactions to flatten amountByCustomer and format createdAt
        const mappedData = data.transactions.map((tx) => ({
          _id: tx._id,
          customerId: tx.customerId,
          amountBy: tx.amountBy,
          amount: tx.amount,
          type: tx.type, // Already mapped to 'Reward' or 'Withdraw'
          createdAt: new Date(tx.createdAt).toLocaleString(),
          amountByCustomerName: tx.amountByCustomer.name,
          amountByCustomerMobile: tx.amountByCustomer.mobile,
          amountByCustomerEmail: tx.amountByCustomer.email || 'N/A',
          paymentMode: tx.paymentMode, // [CASH-MODE]
        }));
        setTransactions(mappedData);
        setTotal(data.total);
        setTotalsByMode(data.totalsByMode || null); // [CASH-MODE]
      } else {
        setTransactions([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching all referral transactions:', error);
      setTransactions([]);
      setTotal(0);
      setSnackbar({
        open: true,
        message: 'Error fetching all referral transactions.',
        severity: 'error',
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAllReferralTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, paymentMode]); // [CASH-MODE] refetch on mode change

  // Function to show snackbar notifications
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Function to close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  // Function to handle printing the referral transactions
  const handlePrint = () => {
    if (transactions.length === 0) {
      showSnackbar('No data to print.', 'error');
      return;
    }

    const doc = new jsPDF('landscape', 'pt', 'a4');

    doc.setFontSize(16);
    doc.text('All Referral Transactions Report', doc.internal.pageSize.getWidth() / 2, 40, {
      align: 'center',
    });

    doc.setFontSize(12);
    const tableColumn = ['Transaction ID', 'Customer ID', 'Referrer Name', 'Referrer Mobile', 'Referrer Email', 'Amount', 'Type', 'Created At', 'Mode']; // [CASH-MODE]
    const tableRows = transactions.map((tx) => [
      tx._id,
      tx.customerId,
      tx.amountByCustomerName,
      tx.amountByCustomerMobile,
      tx.amountByCustomerEmail,
      tx.amount,
      tx.type,
      tx.createdAt,
      tx.type === 'Withdraw' ? '-' : paymentModeLabel(tx), // [CASH-MODE] withdrawals carry no mode
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 80,
      theme: 'grid',
      headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      styles: { fontSize: 12 },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(
        `Generated on: ${new Date().toLocaleDateString()}`,
        30,
        doc.internal.pageSize.getHeight() - 10
      );
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() - 70,
        doc.internal.pageSize.getHeight() - 10
      );
    }

    doc.save('All_Referral_Transactions_Report.pdf');
  };

  // Define columns for DataGrid
  const columns = [
    { field: 'amountByCustomerName', headerName: 'Referrer Name', width: 200 },
    { field: 'amountByCustomerMobile', headerName: 'Referrer Mobile', width: 150 },
    { field: 'amountByCustomerEmail', headerName: 'Referrer Email', width: 200 },
    { field: 'amount', headerName: 'Amount', width: 150 },
    { field: 'type', headerName: 'Type', width: 150 },
    { field: 'createdAt', headerName: 'Time', width: 200 },
    // [CASH-MODE] UPI / Cash badge (withdrawals have no mode)
    {
      field: 'paymentMode',
      headerName: 'Mode',
      width: 100,
      renderCell: (params) => (params.row.type === 'Withdraw' ? '—' : <PaymentModeChip row={params.row} />),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
         <Box sx={{ position: 'relative', padding: 2 }}>
                      {/* Place BackButton outside the Grid */}
                      <BackButton />
                      <br/>  <br/>
      <Typography variant="h5" align="center" gutterBottom>
        All Referral Transactions
      </Typography>
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: '20px' }}>
        <Grid item xs={12} sm={3}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            fullWidth
          >
            Print Report
          </Button>
        </Grid>
        {/* [CASH-MODE] */}
        <Grid item xs={12} sm={3}>
          <PaymentModeFilter
            value={paymentMode}
            onChange={(m) => { setPaymentMode(m); setPage(1); }}
            fullWidth
          />
        </Grid>
      </Grid>

      {/* [CASH-MODE] reward rows split by UPI / Cash (current page when API has no totals) */}
      <PaymentModeTotals
        totals={totalsByMode || totalsByModeFromRows(transactions.filter((t) => t.type !== 'Withdraw'), (t) => t.amount)}
        amountKey="amount"
        title="Rewards"
      />

      {loading ? (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <CircularProgress color="primary" />
        </div>
      ) : (
        <div style={{ height: 600, width: '100%' }}>
          {transactions.length > 0 ? (
            <DataGrid
              rows={transactions}
              columns={columns}
              pageSize={pageSize}
              rowsPerPageOptions={[20, 40, 60]}
              pagination
              paginationMode="server"
              rowCount={total}
              onPageChange={(newPage) => setPage(newPage + 1)} // DataGrid pages are zero-based
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPage(1); // Reset to first page
              }}
              getRowId={(row) => row._id}
              disableSelectionOnClick
            />
          ) : (
            <Typography variant="h6" align="center" style={{ marginTop: '20px' }}>
              No referral transactions found.
            </Typography>
          )}
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </div>
  );
};

export default ReferalAllTransactions;
